import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('SwiperIntegration', LogLevel.INFO);

// Глобальные переменные для экземпляров свайперов
let tariffSwiperInstance: Swiper | null = null;
let tariffCompareSwiperInstance: Swiper | null = null;

/**
 * Инициализирует свайпер для тарифов
 */
export function initTariffSwiper(modalContent: HTMLElement): void {
  const container = modalContent.querySelector('.tariff__items.swiper');
  if (!container) {
    logger.warn('Контейнер для свайпера тарифов не найден');
    return;
  }

  // Проверка структуры слайдера
  const wrapper = container.querySelector('.swiper-wrapper');
  if (!wrapper) {
    logger.error('swiper-wrapper не найден внутри .tariff__items.swiper');
    return;
  }
  const slides = wrapper.querySelectorAll('.swiper-slide');
  if (!slides.length) {
    logger.error('Нет .swiper-slide внутри .swiper-wrapper');
    return;
  }
  let invalidSlide = false;
  slides.forEach(slide => {
    if (!(slide instanceof Element)) {
      logger.error('Один из слайдов не является Element:', slide);
      invalidSlide = true;
    }
  });
  if (invalidSlide) {
    logger.error('Обнаружены невалидные слайды, Swiper не будет инициализирован');
    return;
  }

  // Удаляем предыдущий экземпляр Swiper, если он был
  if ((container as any).swiper) {
    (container as any).swiper.destroy(true, true);
  }

  logger.info('Инициализация свайпера тарифов');

  tariffSwiperInstance = new Swiper(container as HTMLElement, {
    modules: [Pagination],
    slidesPerView: 1,
    spaceBetween: 24,
    pagination: {
      el: modalContent.querySelector('.swiper-pagination'),
      clickable:   true,
    },
    breakpoints: {
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView: 3,
      }
    },
    on: {
      slideChange: function () {
        logger.info('Изменение слайда тарифов', this.activeIndex);
        syncTariffSwitchers(modalContent, this.activeIndex);
        syncCustomPagination(modalContent, this.activeIndex);
        if (tariffCompareSwiperInstance && tariffCompareSwiperInstance.activeIndex !== this.activeIndex) {
          tariffCompareSwiperInstance.slideTo(this.activeIndex);
        }
      }
    }
  });
  
  // Инициализируем синхронизацию переключателей
  initSwitchersSync(modalContent);
}

/**
 * Инициализирует свайпер для сравнения тарифов
 */
export function initTariffCompareSwiper(modalContent: HTMLElement): void {
  const container = modalContent.querySelector('.tariff__compare.swiper');
  if (!container) {
    logger.warn('Контейнер для свайпера сравнения не найден');
    return;
  }

  // Проверка структуры слайдера
  const wrapper = container.querySelector('.swiper-wrapper');
  if (!wrapper) {
    logger.error('swiper-wrapper не найден внутри .tariff__compare.swiper');
    return;
  }
  const slides = wrapper.querySelectorAll('.swiper-slide');
  if (!slides.length) {
    logger.error('Нет .swiper-slide внутри .tariff__compare .swiper-wrapper');
    return;
  }
  let invalidSlide = false;
  slides.forEach(slide => {
    if (!(slide instanceof Element)) {
      logger.error('Один из слайдов сравнения не является Element:', slide);
      invalidSlide = true;
    }
  });
  if (invalidSlide) {
    logger.error('Обнаружены невалидные слайды сравнения, Swiper не будет инициализирован');
    return;
  }

  if ((container as any).swiper) {
    (container as any).swiper.destroy(true, true);
  }

  logger.info('Инициализация свайпера сравнения тарифов');

  tariffCompareSwiperInstance = new Swiper(container as HTMLElement, {
    modules: [Navigation, Pagination],
    slidesPerView: 1,
    spaceBetween: 24,
    navigation: {
      nextEl: modalContent.querySelector('.tariff__compare .swiper-button-next'),
      prevEl: modalContent.querySelector('.tariff__compare .swiper-button-prev'),
    },
    pagination: {
      el: modalContent.querySelector('.tariff__compare .swiper-pagination'),
      clickable: true,
    },
    breakpoints: {
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView: 3,
      }
    },
    on: {
      slideChange: function () {
        logger.info('Изменение слайда сравнения', this.activeIndex);
        syncTariffSwitchers(modalContent, this.activeIndex);
        syncCustomPagination(modalContent, this.activeIndex);
        if (tariffSwiperInstance && tariffSwiperInstance.activeIndex !== this.activeIndex) {
          tariffSwiperInstance.slideTo(this.activeIndex);
        }
      }
    }
  });
  
  // Инициализируем синхронизацию переключателей
  initSwitchersSync(modalContent);
}

/**
 * Инициализирует синхронизацию переключателей
 */
function initSwitchersSync(modalContent: HTMLElement): void {
  // Удаляем предыдущие обработчики, чтобы избежать дублирования
  const switchers = modalContent.querySelectorAll('.header__tarif__switcher__items p');
  const customPaginations = modalContent.querySelectorAll('.custom-pagination__item');
  
  // Клонируем элементы для удаления всех обработчиков
  switchers.forEach((el) => {
    const newEl = el.cloneNode(true);
    el.parentNode?.replaceChild(newEl, el);
  });
  
  customPaginations.forEach((el) => {
    const newEl = el.cloneNode(true);
    el.parentNode?.replaceChild(newEl, el);
  });
  
  // Получаем обновленные элементы после клонирования
  const newSwitchers = modalContent.querySelectorAll('.header__tarif__switcher__items p');
  const newCustomPaginations = modalContent.querySelectorAll('.custom-pagination__item');
  
  if (newSwitchers.length) {
    newSwitchers.forEach((el, idx) => {
      el.addEventListener('click', () => {
        logger.info('Клик по переключателю тарифа', idx);
        if (tariffSwiperInstance) tariffSwiperInstance.slideTo(idx);
        if (tariffCompareSwiperInstance) tariffCompareSwiperInstance.slideTo(idx);
        syncTariffSwitchers(modalContent, idx);
        syncCustomPagination(modalContent, idx);
      });
    });
    syncTariffSwitchers(modalContent, 0);
    syncCustomPagination(modalContent, 0);
  }
  
  if (newCustomPaginations.length) {
    newCustomPaginations.forEach((el, idx) => {
      el.addEventListener('click', () => {
        logger.info('Клик по пагинации', idx);
        if (tariffSwiperInstance) tariffSwiperInstance.slideTo(idx);
        if (tariffCompareSwiperInstance) tariffCompareSwiperInstance.slideTo(idx);
        syncTariffSwitchers(modalContent, idx);
        syncCustomPagination(modalContent, idx);
      });
    });
  }
}

/**
 * Синхронизирует переключатели тарифов
 */
function syncTariffSwitchers(modalContent: HTMLElement, activeIdx: number): void {
  const switchers = modalContent.querySelectorAll('.header__tarif__switcher__items p');
  switchers.forEach((el, idx) => {
    if (idx === activeIdx) {
      el.classList.add('active');
    } else {
      el.classList.remove('active');
    }
  });
}

/**
 * Синхронизирует кастомную пагинацию
 */
function syncCustomPagination(modalContent: HTMLElement, activeIdx: number): void {
  const customPaginations = modalContent.querySelectorAll('.custom-pagination__item');
  customPaginations.forEach((el, idx) => {
    if (idx === activeIdx) {
      el.classList.add('active');
    } else {
      el.classList.remove('active');
    }
  });
}

/**
 * Уничтожает все экземпляры свайперов
 */
export function destroySwipers(): void {
  if (tariffSwiperInstance) {
    tariffSwiperInstance.destroy(true, true);
    tariffSwiperInstance = null;
  }
  if (tariffCompareSwiperInstance) {
    tariffCompareSwiperInstance.destroy(true, true);
    tariffCompareSwiperInstance = null;
  }
  logger.info('Свайперы уничтожены');
}

export function getTariffSwiperInstance(): Swiper | null {
  return tariffSwiperInstance;
} 